import { computed, onScopeDispose, ref, watch, type ComputedRef, type Ref } from 'vue'

import { creativeWatchlistApi, type CreativeWatchlistApi } from '../api/CreativeWatchlistApi'
import { SaveAtcWatchlistFlow, type SaveAtcWatchlistOutcome } from '../flows/SaveAtcWatchlistFlow'
import type {
  AtcAdvertiserSuggestion,
  AtcDraft,
  AtcField,
  AtcPayloadConfig,
} from '../models/watchlist.types'
import {
  createAtcDraft,
  normalizeAdvertiserIds,
  normalizeSavedConfigs,
  validateDrafts,
} from '../service/atcConfig'

import { useAccount, useWorkspace } from '@/features/account'
import { useI18n } from '@/shared/i18n'
import { useConfirm } from '@/shared/ui'

type AtcAsyncState = 'loading' | 'empty' | 'error' | 'ready'

export interface UseAtcWatchlist {
  state: Ref<AtcAsyncState>
  query: Ref<string>
  suggestions: Ref<AtcAdvertiserSuggestion[]>
  suggestionsLoading: Ref<boolean>
  savedConfigs: Ref<AtcPayloadConfig[]>
  drafts: Ref<AtcDraft[]>
  hasExistingConfig: Ref<boolean>
  expandedUid: Ref<string | null>
  saving: Ref<boolean>
  hasSubmitted: Ref<boolean>
  loadErrorKey: Ref<string | null>
  saveErrorKey: Ref<string | null>
  addErrorKey: Ref<string | null>
  validation: ComputedRef<ReturnType<typeof validateDrafts>>
  canSave: ComputedRef<boolean>
  atCap: ComputedRef<boolean>
  load: () => Promise<void>
  resetDrafts: () => void
  setQuery: (value: string) => void
  addFromSearch: (suggestion?: AtcAdvertiserSuggestion) => boolean
  removeDraft: (uid: string) => void
  toggleDraft: (uid: string) => void
  patchDraft: (uid: string, patch: Partial<AtcDraft>) => void
  touchField: (uid: string, field: AtcField) => void
  save: () => Promise<SaveAtcWatchlistOutcome>
}

interface UseAtcWatchlistOptions {
  api?: CreativeWatchlistApi
  debounceMs?: number
}

function requestKind(cause: unknown): string | null {
  if (!cause || typeof cause !== 'object') return null
  const kind = (cause as { kind?: unknown }).kind
  return typeof kind === 'string' ? kind : null
}

export function useAtcWatchlist(options: UseAtcWatchlistOptions = {}): UseAtcWatchlist {
  const api = options.api ?? creativeWatchlistApi
  const debounceMs = options.debounceMs ?? 300
  const account = useAccount()
  const workspace = useWorkspace()
  const confirm = useConfirm()
  const { t } = useI18n()

  const state = ref<AtcAsyncState>('loading')
  const query = ref('')
  const suggestions = ref<AtcAdvertiserSuggestion[]>([])
  const suggestionsLoading = ref(false)
  const savedConfigs = ref<AtcPayloadConfig[]>([])
  const drafts = ref<AtcDraft[]>([])
  const hasExistingConfig = ref(false)
  const expandedUid = ref<string | null>(null)
  const saving = ref(false)
  const hasSubmitted = ref(false)
  const loadErrorKey = ref<string | null>(null)
  const saveErrorKey = ref<string | null>(null)
  const addErrorKey = ref<string | null>(null)
  let sequence = 0
  let loadController: AbortController | null = null
  let searchController: AbortController | null = null
  let searchTimer: ReturnType<typeof setTimeout> | null = null

  const validation = computed(() => validateDrafts(drafts.value))
  const atCap = computed(() => drafts.value.length >= 10)
  const canSave = computed(
    () =>
      !saving.value &&
      validation.value.valid &&
      (hasExistingConfig.value || drafts.value.length > 0),
  )

  function nextUid(): string {
    sequence += 1
    return `atc-${sequence}`
  }

  function cloneConfigs(configs: readonly AtcPayloadConfig[]): AtcDraft[] {
    return normalizeSavedConfigs([...configs]).map((config) => createAtcDraft(nextUid(), config))
  }

  function resetDrafts(): void {
    drafts.value = savedConfigs.value.length
      ? cloneConfigs(savedConfigs.value)
      : [createAtcDraft(nextUid())]
    expandedUid.value = drafts.value[0]?.uid ?? null
    hasSubmitted.value = false
    saveErrorKey.value = null
    addErrorKey.value = null
  }

  function setQuery(value: string): void {
    query.value = value
  }

  async function load(): Promise<void> {
    const accountId = account.accountId.value
    const workspaceId = workspace.workspaceId.value
    loadController?.abort()
    if (accountId === undefined || workspaceId === undefined) {
      savedConfigs.value = []
      hasExistingConfig.value = false
      state.value = 'empty'
      return
    }

    const controller = new AbortController()
    loadController = controller
    state.value = 'loading'
    loadErrorKey.value = null
    try {
      const snapshot = await api.getAtcConfigSnapshot(accountId, workspaceId, controller.signal)
      if (controller.signal.aborted) return
      savedConfigs.value = snapshot.configs
      hasExistingConfig.value = snapshot.exists
      state.value = snapshot.configs.length ? 'ready' : 'empty'
    } catch (cause) {
      if (requestKind(cause) === 'canceled' || controller.signal.aborted) return
      loadErrorKey.value =
        requestKind(cause) === 'forbidden'
          ? 'workspaceDefinitions.watchlist.atc.error.forbidden'
          : 'workspaceDefinitions.watchlist.atc.error.load'
      state.value = 'error'
    }
  }

  function addFromSearch(suggestion?: AtcAdvertiserSuggestion): boolean {
    addErrorKey.value = null
    if (atCap.value) {
      addErrorKey.value = 'workspaceDefinitions.watchlist.atc.error.cap'
      return false
    }
    const raw = query.value.trim()
    const advertiserId = suggestion?.advertiser_id ?? (/^AR\d+$/iu.test(raw) ? raw : '')
    const name = suggestion?.advertiser_name ?? (advertiserId ? '' : raw)
    if (!advertiserId && !name) return false
    const normalizedId = normalizeAdvertiserIds(advertiserId)[0] ?? ''
    if (
      normalizedId &&
      drafts.value.some(
        (draft) => draft.advertiserIdInput.trim().toUpperCase() === normalizedId.toUpperCase(),
      )
    ) {
      addErrorKey.value = 'workspaceDefinitions.watchlist.atc.error.duplicate'
      query.value = ''
      suggestions.value = []
      return false
    }
    const draft = createAtcDraft(nextUid(), {
      config_name: name,
      advertiser_ids: advertiserId ? [advertiserId] : [],
    })
    drafts.value.unshift(draft)
    expandedUid.value = draft.uid
    query.value = ''
    suggestions.value = []
    return true
  }

  function removeDraft(uid: string): void {
    const index = drafts.value.findIndex((draft) => draft.uid === uid)
    if (index < 0) return
    drafts.value.splice(index, 1)
    if (expandedUid.value === uid) {
      expandedUid.value = drafts.value[index]?.uid ?? drafts.value[index - 1]?.uid ?? null
    }
  }

  function toggleDraft(uid: string): void {
    expandedUid.value = expandedUid.value === uid ? null : uid
  }

  function patchDraft(uid: string, patch: Partial<AtcDraft>): void {
    const index = drafts.value.findIndex((draft) => draft.uid === uid)
    const current = drafts.value[index]
    if (index < 0 || !current) return
    drafts.value[index] = { ...current, ...patch }
  }

  function touchField(uid: string, field: AtcField): void {
    const draft = drafts.value.find((item) => item.uid === uid)
    if (!draft) return
    draft.touched = { ...draft.touched, [field]: true }
  }

  async function save(): Promise<SaveAtcWatchlistOutcome> {
    const accountId = account.accountId.value
    const workspaceId = workspace.workspaceId.value
    if (accountId === undefined || workspaceId === undefined) return { status: 'unchanged' }
    hasSubmitted.value = true
    saveErrorKey.value = null
    saving.value = true
    try {
      const outcome = await new SaveAtcWatchlistFlow(api).execute({
        accountId,
        workspaceId,
        drafts: drafts.value,
        hasExistingConfig: hasExistingConfig.value,
        confirmDelete: () =>
          confirm({
            title: t('workspaceDefinitions.watchlist.atc.deleteAll.title'),
            message: t('workspaceDefinitions.watchlist.atc.deleteAll.confirm'),
            confirmLabel: t('workspaceDefinitions.watchlist.atc.deleteAll.action'),
            cancelLabel: t('common.cancel'),
            danger: true,
          }),
      })
      if (outcome.status === 'saved') {
        savedConfigs.value = outcome.configs
        hasExistingConfig.value = true
        state.value = outcome.configs.length ? 'ready' : 'empty'
        resetDrafts()
      } else if (outcome.status === 'deleted') {
        savedConfigs.value = []
        hasExistingConfig.value = false
        state.value = 'empty'
        drafts.value = []
        expandedUid.value = null
      }
      return outcome
    } catch (cause) {
      if (requestKind(cause) !== 'canceled') {
        saveErrorKey.value =
          requestKind(cause) === 'forbidden'
            ? 'workspaceDefinitions.watchlist.atc.error.forbidden'
            : 'workspaceDefinitions.watchlist.atc.error.save'
      }
      return { status: 'unchanged' }
    } finally {
      saving.value = false
    }
  }

  watch(
    query,
    (value) => {
      if (searchTimer) clearTimeout(searchTimer)
      searchController?.abort()
      suggestions.value = []
      const accountId = account.accountId.value
      const workspaceId = workspace.workspaceId.value
      if (value.trim().length < 2 || accountId === undefined || workspaceId === undefined) return
      searchTimer = setTimeout(async () => {
        const controller = new AbortController()
        searchController = controller
        suggestionsLoading.value = true
        try {
          suggestions.value = await api.searchAtcAdvertisers(
            accountId,
            workspaceId,
            value.trim(),
            controller.signal,
          )
        } catch (cause) {
          if (requestKind(cause) !== 'canceled' && !controller.signal.aborted) {
            addErrorKey.value = 'workspaceDefinitions.watchlist.atc.error.search'
          }
        } finally {
          if (!controller.signal.aborted) suggestionsLoading.value = false
        }
      }, debounceMs)
    },
    { flush: 'post' },
  )

  watch([account.accountId, workspace.workspaceId], () => void load(), { immediate: true })
  onScopeDispose(() => {
    loadController?.abort()
    searchController?.abort()
    if (searchTimer) clearTimeout(searchTimer)
  })

  return {
    state,
    query,
    suggestions,
    suggestionsLoading,
    savedConfigs,
    drafts,
    hasExistingConfig,
    expandedUid,
    saving,
    hasSubmitted,
    loadErrorKey,
    saveErrorKey,
    addErrorKey,
    validation,
    canSave,
    atCap,
    load,
    resetDrafts,
    setQuery,
    addFromSearch,
    removeDraft,
    toggleDraft,
    patchDraft,
    touchField,
    save,
  }
}
